import React, { useEffect, useState, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from '@material-ui/core';
import axios from 'axios';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';

export const SelectUndeployProduct = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formData, required, rawErrors, schema, formContext } = props;

  // Products live under the Backstage project (`project_name`), not the ADO project —
  // the SSM key is /backstage/project/{project_name}.
  const resolvedProjectName = useMemo(() => {
    // Try to get projectName from various possible locations
    const projectName =
      formContext.formData?.projectName ||
      (formContext as any).rootFormData?.projectName ||
      '';

    console.log('SelectUndeployProduct: resolved projectName =', projectName);
    console.log('SelectUndeployProduct: formContext.formData =', formContext.formData);

    return projectName;
  }, [formContext]);

  const endpoint = `/backstage/project/${resolvedProjectName}`;

  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  useEffect(() => {
    // Reset options and fetch error.
    setOptions([]);
    setFetchError(null);

    // If no project is selected, skip fetching and set a fetch error.
    if (!resolvedProjectName) {
      setLoading(false);
      setFetchError('Please select a valid Project Name');
      return;
    }

    setLoading(true);
    async function fetchProducts() {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const credentials = await identityApi.getCredentials();
        const token = credentials.token;
        const response = await axios.post(
          `${baseUrl}/aws/params`,
          { name: endpoint },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const rawValue = response.data.value || '';
        if (!rawValue) throw new Error('Invalid response: No value returned');

        const parsed = yaml.load(rawValue);
        let productList: string[] = [];
        if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).products)) {
          productList = (parsed as any).products;
        } else if (typeof parsed === 'string') {
          productList = parsed.split(',').map((p: string) => p.trim()).filter((p: string) => p);
        } else {
          throw new Error('Products list not found in response');
        }

        setOptions(productList);
        setFetchError(null);
      } catch (err: any) {
        console.error('Error fetching products for undeploy:', err);
        setFetchError('Failed to fetch products. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, [resolvedProjectName, endpoint, identityApi, discoveryApi]);

  return (
    <FormControl fullWidth required={required} error={!!rawErrors?.length || !!fetchError}>
      <InputLabel>{schema.title}</InputLabel>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Select
          value={formData || ''}
          onChange={(e) => onChange(e.target.value as string)}
          disabled={!resolvedProjectName}
        >
          {options.map((option, idx) => (
            <MenuItem key={idx} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      )}
      {fetchError && <FormHelperText>{fetchError}</FormHelperText>}
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText>{rawErrors.join(', ')}</FormHelperText>
      )}
    </FormControl>
  );
};

